import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { Accordion } from "react-bootstrap";
import { isCanvasElement, isPage, isSection, isSlide } from "../../../../common/utils";
import { getSuggestedSlideDurationSec } from "../../../../common/scene_duration.es6";
import IdealStudio from "../../../../core/editor/main";
import "./_viewToolbar.scss";
import { renderAccordion } from "../../../../core/editor/accordion_provider";
// === Task 18 Skip Interactive ===
import { isInteractiveScene } from "../../../../_visor/utils/recorderMode";
import SkipInteractiveCheckbox from "../../mandatory_settings/SkipInteractiveCheckbox";
// === End Task 18 Skip Interactive ===

function ViewToolbar(props) {
  const {
    navItems,
    navItemSelected,
    containedViews,
    containedViewSelected,
    exercises = {},
    viewToolbars,
    boxes,
    pluginToolbars,
    animationsById,
    soundList,
    updateViewToolbar,
    store,
    globalConfig,
    activeAccordionKey,
  } = props;

  const [availableMandatoryTypes, setAvailableMandatoryTypes] = useState({
    types: [],
    labels: [],
    disabledTypes: [],
  });
  const [activeKey, setActiveKey] = useState(activeAccordionKey);
  const [highlightedAccordion, setHighlightedAccordion] = useState(null);

  useEffect(() => {
    if (activeAccordionKey) {
      setActiveKey(activeAccordionKey);
      setHighlightedAccordion(activeAccordionKey);
      const timer = setTimeout(() => {
        setHighlightedAccordion(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [activeAccordionKey]);

  const currentId = useMemo(
    () => (containedViewSelected !== 0 ? containedViewSelected : navItemSelected),
    [containedViewSelected, navItemSelected]
  );

  const viewToolbar = viewToolbars?.[currentId];

  const pageObj = useMemo(() => {
    return containedViewSelected !== 0
      ? containedViews[containedViewSelected]
      : navItems[navItemSelected];
  }, [containedViewSelected, containedViews, navItemSelected, navItems]);

  const getRelevantState = useCallback(() => {
    const id = currentId;
    const navItem = navItems?.[id] || containedViews?.[id];
    const pageBoxes = navItem?.boxes || [];

    if (!animationsById && !boxes) return null;

    return {
      animationsById: pageBoxes.reduce((acc, boxId) => {
        if (animationsById?.[boxId]) {
          acc[boxId] = Object.keys(animationsById[boxId]).length;
        }
        return acc;
      }, {}),
      boxAnimations: pageBoxes.reduce((acc, boxId) => {
        const box = boxes?.[boxId];
        if (box) {
          acc[boxId] = {
            hasBoxAnimation: !!(box.boxAnimation && box.boxAnimation.animation),
            hasTextAnimation: !!(box.textAnimation && box.textAnimation.animation),
            boxAnimationData: box.boxAnimation ? JSON.stringify(box.boxAnimation) : null,
            textAnimationData: box.textAnimation ? JSON.stringify(box.textAnimation) : null,
          };
        }
        return acc;
      }, {}),
      pluginToolbarsById: pageBoxes.reduce((acc, boxId) => {
        if (pluginToolbars?.[boxId]) {
          acc[boxId] = {
            pluginId: pluginToolbars[boxId].pluginId,
            soundListLength: pluginToolbars[boxId].state?.soundList?.length || 0,
          };
        }
        return acc;
      }, {}),
      soundList: (() => {
        const pageSounds = soundList?.[id];
        if (Array.isArray(pageSounds)) return pageSounds.length;
        if (pageSounds) return Object.keys(pageSounds).length;
        return 0;
      })(),
      exercises: exercises?.[id] ? Object.keys(exercises[id].exercises || {}).length : 0,
    };
  }, [
    animationsById,
    boxes,
    containedViews,
    currentId,
    exercises,
    navItems,
    pluginToolbars,
    soundList,
  ]);

  const hasQuestionsInScene = useCallback(
    (pageId) => {
      const navItem = navItems[pageId] || containedViews[pageId];
      if (!navItem?.boxes) return false;

      const state = store?.getState?.()?.undoGroup?.present || IdealStudio.State;
      const pluginToolbarsById = state?.pluginToolbarsById || pluginToolbars;
      if (!pluginToolbarsById) return false;

      const questionPlugins = [
        "MultipleAnswer",
        "MultipleChoice",
        "FreeResponse",
        "DragDrop",
        "FillInBlank",
        "InputText",
        "Matching",
        "TrueFalse",
        "Ordering",
      ];

      const hasPluginQuestions = navItem.boxes.some((boxId) => {
        const plugin = pluginToolbarsById[boxId];
        return plugin && questionPlugins.includes(plugin.pluginId);
      });

      const hasExerciseEntries =
        exercises &&
        exercises[pageId] &&
        exercises[pageId].exercises &&
        Object.keys(exercises[pageId].exercises).length > 0;

      return hasPluginQuestions || hasExerciseEntries;
    },
    [containedViews, exercises, navItems, pluginToolbars, store]
  );

  const hasOneClickMarkersInScene = useCallback(
    (pageId) => {
      const navItem = navItems[pageId] || containedViews[pageId];
      if (!navItem?.boxes) return false;
      if (!pluginToolbars) return false;
      return navItem.boxes.some((boxId) => {
        const plugin = pluginToolbars[boxId];
        return plugin && plugin.pluginId === "OneClick";
      });
    },
    [containedViews, navItems, pluginToolbars]
  );

  // waitForSound / waitForVideo / waitForAnimation are deprecated: they can no
  // longer be selected, but legacy trainings that still store one keep it
  // visible as a checked, disabled option (Visor behavior is unchanged).
  // TODO: deprecated — remove RETIRED_TYPES handling once no trainings still
  // use the old mandatory types (waitForSound/waitForVideo/waitForAnimation).
  const getAvailableMandatoryTypes = useCallback(
    (pageId) => {
      const RETIRED_TYPES = ["waitForSound", "waitForVideo", "waitForAnimation"];
      const SELECTABLE_TYPES = [
        { type: "waitForTime", available: () => true },
        { type: "waitForOneClick", available: () => hasOneClickMarkersInScene(pageId) },
        { type: "waitForAction", available: () => hasQuestionsInScene(pageId) },
      ];

      const types = [];
      const labels = [];
      const disabledTypes = [];

      SELECTABLE_TYPES.forEach(({ type, available }) => {
        if (available()) {
          types.push(type);
          labels.push(i18n.t(type));
        }
      });

      const currentType = viewToolbars?.[pageId]?.mandatoryType;
      if (RETIRED_TYPES.includes(currentType)) {
        types.push(currentType);
        labels.push(i18n.t(currentType));
        disabledTypes.push(currentType);
      }

      return { types, labels, disabledTypes };
    },
    [hasOneClickMarkersInScene, hasQuestionsInScene, viewToolbars]
  );

  const updateAvailableTypes = useCallback(() => {
    const newAvailableTypes = getAvailableMandatoryTypes(currentId);
    setAvailableMandatoryTypes((prev) => {
      const typesChanged =
        newAvailableTypes.types.length !== prev.types.length ||
        newAvailableTypes.types.some((type, index) => type !== prev.types[index]);
      const labelsChanged =
        newAvailableTypes.labels.length !== prev.labels.length ||
        newAvailableTypes.labels.some((label, index) => label !== prev.labels[index]);
      const prevDisabled = prev.disabledTypes || [];
      const disabledChanged =
        newAvailableTypes.disabledTypes.length !== prevDisabled.length ||
        newAvailableTypes.disabledTypes.some((type, index) => type !== prevDisabled[index]);

      if (typesChanged || labelsChanged || disabledChanged) {
        return newAvailableTypes;
      }
      return prev;
    });
  }, [currentId, getAvailableMandatoryTypes]);

  // Update available types on relevant changes
  useEffect(() => {
    updateAvailableTypes();
  }, [updateAvailableTypes, animationsById, boxes, pluginToolbars, soundList]);

  // Ensure mandatory defaults
  useEffect(() => {
    if (!viewToolbar) return;
    const DEFAULT_MANDATORY_TYPE = "waitForTime";
    const DEFAULT_MIN_TIME = 3;

    const needsUpdate =
      !viewToolbar.mandatoryType ||
      viewToolbar.minTime === undefined ||
      viewToolbar.minTime === null;

    if (needsUpdate) {
      const updatePayload = {};
      if (!viewToolbar.mandatoryType) {
        updatePayload.mandatoryType = DEFAULT_MANDATORY_TYPE;
      }
      if (viewToolbar.minTime === undefined || viewToolbar.minTime === null) {
        updatePayload.minTime = DEFAULT_MIN_TIME;
      }
      if (Object.keys(updatePayload).length > 0) {
        updateViewToolbar(currentId, updatePayload);
      }
    }
  }, [currentId, updateViewToolbar, viewToolbar]);

  // Derived info (currently not rendered, but kept for parity)
  const relevantState = useMemo(() => getRelevantState(), [getRelevantState]);

  if (!pageObj || !isCanvasElement(navItemSelected, IdealStudio.Config.sections_have_content)) {
    return null;
  }

  const type = pageObj.type;
  const isContainedView = containedViewSelected !== 0;

  let doc_type = "";
  if (isPage(currentId)) {
    doc_type = i18n.t("page");
  } else if (isSlide(type)) {
    doc_type = i18n.t("slide");
  } else if (isSection(currentId)) {
    doc_type = i18n.t("section");
  }

  const viewToolbarData = viewToolbar;
  const controls = {
    main: {
      __name: "Main",
      accordions: {
        __basic: {
          __name: i18n.t("General_name"),
          buttons: {
            navitem_name: {
              __name: i18n.t("NavItem_name"),
              type: "text",
              value: viewToolbarData.viewName,
            },
            navitem_isMandatory: {
              __name: i18n.t("isMandatory"),
              type: "checkbox",
              checked: viewToolbarData.isMandatory,
            },
            navitem_mandatoryType: {
              __name: i18n.t("mandatoryType"),
              type: "radio",
              hide: !viewToolbarData.isMandatory || availableMandatoryTypes.types.length === 0,
              value:
                (viewToolbarData.mandatoryType === "waitForCorrectAction"
                  ? "waitForAction"
                  : viewToolbarData.mandatoryType) || "waitForTime",
              options: availableMandatoryTypes.types,
              labels: availableMandatoryTypes.labels,
              disabledOptions: availableMandatoryTypes.disabledTypes,
            },
            navitem_waitForCorrectAnswer: {
              __name: i18n.t("waitForCorrectAction"),
              type: "checkbox",
              // Also requires waitForAction to be AVAILABLE: imported content can
              // store a question type on a scene with no question — the checkbox
              // must not surface from that stale value alone.
              hide:
                !viewToolbarData.isMandatory ||
                !availableMandatoryTypes.types.includes("waitForAction") ||
                (viewToolbarData.mandatoryType !== "waitForAction" &&
                  viewToolbarData.mandatoryType !== "waitForCorrectAction"),
              checked: viewToolbarData.mandatoryType === "waitForCorrectAction",
            },
            navitem_slideDuration: {
              __name: i18n.t("slideDuration"),
              type: "number",
              allowEmpty: true,
              hide: !viewToolbarData.isMandatory || viewToolbarData.mandatoryType !== "waitForTime",
              value:
                typeof viewToolbarData.slideDuration === "number" &&
                viewToolbarData.slideDuration > 0
                  ? viewToolbarData.slideDuration
                  : "",
              placeholder:
                getSuggestedSlideDurationSec(currentId, {
                  viewToolbarsById: viewToolbars,
                  navItemsById: navItems,
                  globalConfig,
                }).toFixed(1) + "s",
            },
            navitem_autoNext: {
              __name: i18n.t("autoNext"),
              type: "checkbox",
              // Independent of isMandatory: the visor auto-advances once the
              // scene content finishes regardless of the mandatory gate.
              // Question scenes never auto-advance (the visor blocks it so the
              // learner can read the answer feedback) — hide the dead toggle.
              hide: hasQuestionsInScene(currentId),
              checked: viewToolbarData.autoNext,
              note: i18n.t("autoNextNote"),
            },
            navitem_autoNextShowCta: {
              __name: i18n.t("autoNextShowCta"),
              type: "checkbox",
              // Opt-in countdown card before the automatic jump. Off (default)
              // the visor advances silently with no CTA at all.
              hide: hasQuestionsInScene(currentId) || !viewToolbarData.autoNext,
              checked: viewToolbarData.autoNextShowCta,
              note: i18n.t("autoNextShowCtaNote"),
            },
            navitem_sceneEndCta: {
              __name: i18n.t("sceneEndCta"),
              type: "checkbox",
              // Scene-end transition card, opt-in (default off): off, the
              // scene stays on screen when it ends and the learner advances
              // with the Next button. With auto-advance on, the countdown
              // toggle above governs the card instead, so hide this one —
              // EXCEPT on a question scene, where auto-advance never runs
              // (the Visor blocks it) and the countdown toggle is hidden too.
              // Keying on autoNext alone there left the author with no card
              // control at all the moment the global config switched
              // auto-advance on course-wide. This is the toggle that governs
              // the card on those scenes; see ctaNextCardOptIn in VisorApp.
              hide: !!viewToolbarData.autoNext && !hasQuestionsInScene(currentId),
              checked: viewToolbarData.sceneEndCta,
              note: i18n.t("sceneEndCtaNote"),
            },
            navitem_applyAllScenes: {
              __name: i18n.t("marks.applyAllScenes"),
              type: "apply_all_scenes",
            },
          },
        },

        __sound: {
          __name: i18n.t("Sound.Sound"),
          buttons: {
            sound_recorder: {
              __name: i18n.t("Sound.soundRecorder"),
              type: "sound_recorder_provider",
            },
            add_sound: {
              __name: i18n.t("Sound.addSound"),
              type: "add_sound",
            },
            sound_list: {
              __name: i18n.t("soundList"),
              type: "sound_list",
            },
            audio_preview_enable: {
              __name: i18n.t("Sound.listenSounds"),
              type: "checkbox",
              checked: viewToolbarData.audio_preview_enable,
            },
            audio_preview: {
              __name: i18n.t("Sound.demoSound"),
              type: "audio_preview",
              value: viewToolbarData.audio_preview_enable,
            },
          },
        },
        // Header accordion (course title / page title / subtitle) disabled per product decision.
        // Headers are managed at the course level only.
      },
    },
  };

  if (!isContainedView && controls?.main?.accordions?.__basic?.buttons) {
    controls.main.accordions.__basic.buttons.page_display = {
      __name: i18n.t("display_page"),
      type: "checkbox",
      checked: !pageObj.hidden,
    };
  }

  if (!isCanvasElement(navItemSelected, IdealStudio.Config.sections_have_content)) {
    return null;
  }

  // === Task 18 Skip Interactive ===
  // The Skip Interactive checkbox only shows on scenes that contain at least
  // one InteractivePlugin item — same predicate the recorder uses.
  const showSkipOption = isInteractiveScene(navItems?.[currentId], boxes, pluginToolbars);
  const skipChecked = !!(viewToolbar && viewToolbar.skipInteractive);
  // === End Task 18 Skip Interactive ===

  return Object.keys(controls).map((tabKey, index) => {
    const tab = controls[tabKey];
    const accordionKeys = Object.keys(tab.accordions).sort();
    return (
      <div key={`key_${index}`} className="toolbarTab">
        <Accordion activeKey={activeKey} onSelect={(key) => setActiveKey(key)}>
          {accordionKeys.map((accordionKey, ind) => {
            const eventKey = `${accordionKey}_${ind}`;
            const isHighlighted = highlightedAccordion === eventKey;
            return (
              <div
                key={eventKey}
                style={
                  isHighlighted
                    ? {
                        animation: "pulse-border 0.8s ease-in-out 4",
                        borderRadius: "4px",
                      }
                    : {}
                }
              >
                {renderAccordion(
                  tab.accordions[accordionKey],
                  tabKey,
                  [accordionKey],
                  viewToolbarData,
                  eventKey,
                  props
                )}
              </div>
            );
          })}
        </Accordion>
        {/* === Task 18 Skip Interactive === */}
        {showSkipOption && (
          <div style={{ padding: "8px 12px" }}>
            <SkipInteractiveCheckbox
              checked={skipChecked}
              onChange={(value) => updateViewToolbar(currentId, { skipInteractive: value })}
            />
          </div>
        )}
        {/* === End Task 18 Skip Interactive === */}
      </div>
    );
  });
}

ViewToolbar.propTypes = {
  navItems: PropTypes.object.isRequired,
  navItemSelected: PropTypes.any.isRequired,
  containedViews: PropTypes.object.isRequired,
  containedViewSelected: PropTypes.any.isRequired,
  exercises: PropTypes.object,
  viewToolbars: PropTypes.object,
  boxes: PropTypes.object,
  pluginToolbars: PropTypes.object,
  animationsById: PropTypes.object,
  soundList: PropTypes.object,
  updateViewToolbar: PropTypes.func.isRequired,
  store: PropTypes.any,
  globalConfig: PropTypes.object,
};

export default ViewToolbar;
